import { Big } from 'big.js';
import { DEFAULT_MASK_RULES, type MaskGenerator } from 'react-hook-mask';
import { memoize } from '@interxfi/utilities/cache/memoize';
import { getCurrencyFormatter } from '@interxfi/utilities/currency/formatting';
import { getIntlNumberFormat } from '@interxfi/utilities/intl/number';
import { parseNumber } from '@interxfi/utilities/number/parse';

export interface MaskOptions extends MaskGenerator {
  emptyValue?: string;
}

const usPhoneMaskTemplate = '(999) 999-9999';

export const yearMask: MaskOptions = {
  rules: DEFAULT_MASK_RULES,
  generateMask: () => '9999',
  emptyValue: 'YYYY',
  keepMask: true,
};

export const monthMask: MaskOptions = {
  rules: DEFAULT_MASK_RULES,
  generateMask: () => '99',
  emptyValue: 'MM',
  keepMask: true,
};

export const dayMask: MaskOptions = {
  rules: DEFAULT_MASK_RULES,
  generateMask: () => '99',
  emptyValue: 'DD',
  keepMask: true,
};

export const dateMask: MaskOptions = {
  rules: DEFAULT_MASK_RULES,
  generateMask: () => '9999-99-99',
  emptyValue: 'YYYY-MM-DD',
  keepMask: true,
};

export const usPhoneMask: MaskOptions = {
  rules: DEFAULT_MASK_RULES,
  generateMask: () => usPhoneMaskTemplate,
  emptyValue: '(000) 000-0000',
  keepMask: true,
};

export const phoneMasksByCountryCode: Record<string, MaskOptions> = {
  '1': usPhoneMask,
};

const ssnMaskTemplate = '999-99-9999';

export const ssnMask: MaskOptions = {
  rules: DEFAULT_MASK_RULES,
  generateMask: () => ssnMaskTemplate,
  emptyValue: 'XXX-XX-XXXX',
  keepMask: true,
};

const itinMaskTemplate = '999-99-9999';

export const itinMask: MaskOptions = {
  rules: DEFAULT_MASK_RULES,
  generateMask: () => itinMaskTemplate,
  emptyValue: 'XXX-XX-XXXX',
  keepMask: true,
};

const einMaskTemplate = '99-9999999';

export const einMask: MaskOptions = {
  rules: DEFAULT_MASK_RULES,
  generateMask: () => einMaskTemplate,
  emptyValue: 'XX-XXXXXXX',
  keepMask: true,
};

export const time24HourMask: MaskOptions = {
  rules: DEFAULT_MASK_RULES,
  generateMask: () => '99:99',
  emptyValue: '00:00',
  keepMask: true,
};

export const time24HourMaskWithSeconds: MaskOptions = {
  rules: DEFAULT_MASK_RULES,
  generateMask: () => '99:99:99',
  emptyValue: '00:00:00',
  keepMask: true,
};

function countTrailingZeros(value: string): number {
  let count = 0;
  for (let i = value.length - 1; i >= 0; i -= 1) {
    if (value[i] === '0') {
      count += 1;
    } else {
      break;
    }
  }

  return count;
}

export type NumberFormatParts = Partial<Record<Intl.NumberFormatPartTypes, string | undefined>>;

interface NumberFormatMaskOptions {
  allowNegative?: boolean;
  placeholderBuilder?: (parts: NumberFormatParts) => string;
  prefixGetter?: (parts: NumberFormatParts) => string | undefined;
  suffixGetter?: (parts: NumberFormatParts) => string | undefined;
  numberPipe?: (value: string) => string;
}

export function getNumberFormatMask(formatter: Intl.NumberFormat, options: NumberFormatMaskOptions = {}): MaskOptions {
  const { allowNegative = false, placeholderBuilder, prefixGetter, suffixGetter, numberPipe = (x) => x } = options;
  const parts = formatter.formatToParts(-123456.789);
  const formatParts = parts.reduce<NumberFormatParts>(
    (accum, part) => {
      switch (part.type) {
        case 'fraction':
        case 'integer':
        case 'exponentInteger':
          return accum;
        default: {
          accum[part.type] = part.value;
          return accum;
        }
      }
    },
    { decimal: '', group: '', currency: '', minusSign: '' },
  );

  const maxFractionDigits = formatter.resolvedOptions().maximumFractionDigits ?? Infinity;
  const minFractionDigits = formatter.resolvedOptions().minimumFractionDigits;

  const placeholder = placeholderBuilder?.(formatParts);
  const prefix = prefixGetter?.(formatParts);
  const suffix = suffixGetter?.(formatParts);

  const parseNumberOptions = {
    decimalSeparator: formatParts.decimal || '.',
    minusSign: formatParts.minusSign || '-',
  };

  return {
    rules: DEFAULT_MASK_RULES,
    generateMask: (value) => {
      const [whole, fractional] = parseNumber(value, parseNumberOptions).split('.');
      const fractionalTrailingZeros = countTrailingZeros(fractional || '');

      if (!whole && !fractional) {
        return [prefix, suffix].filter(Boolean).join('');
      }

      const formattedNumberParts = formatter.formatToParts(
        numberPipe(`${Number(whole || 0)}${fractional ? `.${fractional}` : ''}`) as unknown as number,
      );

      const builtParts = [...(prefix ? [prefix] : [])];

      let hasSeenFractionalParts = false;
      let hasSeenDecimal = false;

      formattedNumberParts.forEach((part) => {
        switch (part.type) {
          case 'integer':
            builtParts.push('9'.repeat(part.value.length));
            break;
          case 'fraction': {
            hasSeenFractionalParts = true;
            const partTrailingZeros = countTrailingZeros(part.value);
            const mergedFractional = `${part.value}${'0'.repeat(Math.max(0, fractionalTrailingZeros - partTrailingZeros))}`;
            const fractionLength = Math.max(Math.min(mergedFractional.length, maxFractionDigits), minFractionDigits || 0);

            builtParts.push('9'.repeat(fractionLength));
            break;
          }
          case 'decimal': {
            hasSeenDecimal = true;
            builtParts.push(part.value);
            break;
          }
          case 'minusSign': {
            if (allowNegative) {
              builtParts.push(part.value);
            }

            break;
          }
          default:
            if (part.value !== prefix && part.value !== suffix) {
              builtParts.push(part.value);
            }

            break;
        }
      });

      if (fractional === '' && maxFractionDigits) {
        builtParts.push(formatParts.decimal || '.');
      }

      if (!hasSeenFractionalParts && fractionalTrailingZeros > 0) {
        const fractionLength = Math.max(Math.min(fractionalTrailingZeros, maxFractionDigits), minFractionDigits || 0);
        builtParts.push(`${!hasSeenDecimal ? formatParts.decimal || '.' : ''}${'9'.repeat(fractionLength)}`);
      }

      if (suffix) {
        builtParts.push(suffix);
      }

      return builtParts.join('');
    },
    transform: (value: string) => {
      if (!value) {
        return '';
      }

      const defaultValue = [prefix, suffix].filter(Boolean).join('');
      if (value === defaultValue) {
        return defaultValue;
      }

      const [whole, fractional] = parseNumber(value, parseNumberOptions).split('.');
      const fractionalTrailingZeros = countTrailingZeros(fractional || '');
      let hasSeenFractionalParts = false;
      let hasSeenDecimal = false;

      const builtParts = [...(prefix ? [prefix] : [])];

      const formattedNumberParts = formatter.formatToParts(
        numberPipe(`${Number(whole || 0)}${fractional ? `.${fractional}` : ''}`) as unknown as number,
      );

      formattedNumberParts.forEach((part) => {
        switch (part.type) {
          case 'fraction': {
            hasSeenFractionalParts = true;
            const partTrailingZeros = countTrailingZeros(part.value);
            const mergedFractional = `${part.value}${'0'.repeat(Math.max(0, fractionalTrailingZeros - partTrailingZeros))}`;
            const fractionLength = Math.max(Math.min(mergedFractional.length, maxFractionDigits), minFractionDigits || 0);

            builtParts.push(mergedFractional.substring(0, fractionLength));
            break;
          }
          case 'decimal': {
            hasSeenDecimal = true;

            builtParts.push(part.value);
            break;
          }
          case 'minusSign': {
            if (allowNegative) {
              builtParts.push(part.value);
            }

            break;
          }
          default:
            if (part.value !== prefix && part.value !== suffix) {
              builtParts.push(part.value);
            }

            break;
        }
      });

      if (fractional === '' && maxFractionDigits) {
        builtParts.push(formatParts.decimal || '.');
      }

      if (!hasSeenFractionalParts && fractionalTrailingZeros > 0) {
        const fractionLength = Math.max(Math.min(fractionalTrailingZeros, maxFractionDigits), minFractionDigits || 0);
        builtParts.push(`${!hasSeenDecimal ? formatParts.decimal || '.' : ''}${'0'.repeat(fractionLength)}`);
      }

      if (suffix) {
        builtParts.push(suffix);
      }

      return builtParts.join('');
    },
    keepMask: true,
    emptyValue: placeholder,
  };
}

export const getNumericMask = memoize((locale: string | undefined = 'en-US', maximumFractionDigits?: number): MaskOptions => {
  const formatter = getIntlNumberFormat(locale, { maximumFractionDigits });
  const minFractionDigits = formatter.resolvedOptions().minimumFractionDigits;

  return getNumberFormatMask(formatter, {
    placeholderBuilder: (parts) =>
      [parts.decimal && minFractionDigits ? `0${parts.decimal}${'0'.repeat(minFractionDigits)}` : ''].filter(Boolean).join(''),
  });
});

export const INTEGER_MASK = getNumericMask('en-US', 0);
export const TEN_FRACTIONAL_DIGIT_FLOAT_MASK = getNumericMask('en-US', 10);
export const FLOAT_MASK = getNumericMask('en-US');

export const getCurrencyMask = (currency: string, locale: string = 'en-US', options?: Intl.NumberFormatOptions): MaskOptions => {
  const formatter = getCurrencyFormatter(locale, { ...options, currency });
  const minFractionDigits = formatter.resolvedOptions().minimumFractionDigits;

  return getNumberFormatMask(formatter, {
    placeholderBuilder: (parts) =>
      [parts.currency, parts.decimal && minFractionDigits ? `0${parts.decimal}${'0'.repeat(minFractionDigits)}` : ''].filter(Boolean).join(''),
    prefixGetter: (parts) => parts.currency,
  });
};

export const USD_CURRENCY_MASK = getCurrencyMask('USD', 'en-US', { minimumFractionDigits: 0 });
export const USD_CURRENCY_GRANULAR_MASK = getCurrencyMask('USD', 'en-US', { minimumFractionDigits: 0, maximumFractionDigits: 10 });
export const USD_CURRENCY_MASK_INTEGER = getCurrencyMask('USD', undefined, { maximumFractionDigits: 0 });

export function getPercentageMask(locale: string | undefined = 'en-US', maximumFractionDigits: number = 0): MaskOptions {
  const formatter = getIntlNumberFormat(locale, { style: 'percent', maximumFractionDigits });
  const minFractionDigits = formatter.resolvedOptions().minimumFractionDigits;

  return getNumberFormatMask(formatter, {
    placeholderBuilder: (parts) =>
      [0, parts.decimal && minFractionDigits ? `0${parts.decimal}${'0'.repeat(minFractionDigits)}` : ''].filter(Boolean).join(''),
    suffixGetter: (parts) => parts.percent || '%',
    numberPipe: (value) => {
      try {
        const asBig = new Big(value);
        return asBig.div(100).toString();
      } catch {
        return value;
      }
    },
  });
}

export const DEFAULT_PERCENTAGE_MASK = getPercentageMask('en-US', 2);
export const NON_FRACTIONAL_PERCENTAGE_MASK = getPercentageMask('en-US', 0);
